// src/components/forms/shared/FormMessageList.tsx

import React, { useId } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getCustomIcon } from '../../../utils/customIcons';
import type { CustomIconName } from '../../../utils/customIcons';

export type FormMessageColor =
  'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

export interface FormMessageItem {
  label: string;
  icon?: CustomIconName;
}

interface FormMessageListProps {
  heading: string;
  items: FormMessageItem[];
  /** Shown level with the heading, on the right (e.g. an Add button). */
  action?: React.ReactNode;
  headingColor?: FormMessageColor;
  headingFontSize?: string;
  headingFontWeight?: number;
  headingUnderline?: boolean;
  itemColor?: FormMessageColor;
  itemFontSize?: string;
  itemFontWeight?: number;
  itemUnderline?: boolean;
  iconSize?: string;
  maxWidth?: string;
}

const underlineSx = {
  textDecoration: 'underline',
  textUnderlineOffset: '0.3rem',
};

const FormMessageList: React.FC<FormMessageListProps> = ({
  heading,
  items,
  action,
  headingColor = 'primary',
  headingFontSize = '2.2rem',
  headingFontWeight = 700,
  headingUnderline = false,
  itemColor = 'error',
  itemFontSize = '2.2rem',
  itemFontWeight = 700,
  itemUnderline = false,
  iconSize = '2.5rem',
  maxWidth = '800px',
}) => {
  const theme = useTheme();
  const headingId = useId();
  const hasItems = items.length > 0;

  if (!hasItems && !action) return null;

  const resolveColor = (color: FormMessageColor) =>
    theme.palette.mode === 'dark'
      ? theme.palette[color].light
      : theme.palette[color].main;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        columnGap: '1rem',
        width: '100%',
        maxWidth,
        mt: 1,
      }}
    >
      {hasItems && (
        <>
          <Typography
            id={headingId}
            sx={{
              gridColumn: 1,
              gridRow: 1,
              alignSelf: 'center',
              pl: { xs: 0, sm: '0.5rem' },
              m: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              fontSize: headingFontSize,
              fontWeight: headingFontWeight,
              color: resolveColor(headingColor),
              ...(headingUnderline && underlineSx),
            }}
          >
            {heading}
          </Typography>
          <Box
            component="ul"
            aria-labelledby={headingId}
            sx={{
              gridColumn: '1 / -1',
              gridRow: 2,
              listStyle: 'none',
              m: 0,
              p: 0,
              pl: { xs: 0, sm: '0.5rem' },
            }}
          >
            {items.map((item) => (
              <Box
                component="li"
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontSize: itemFontSize,
                  fontWeight: itemFontWeight,
                  lineHeight: 1.5,
                  color: resolveColor(itemColor),
                }}
              >
                <Box component="span" sx={itemUnderline ? underlineSx : {}}>
                  {item.label}
                </Box>
                {item.icon && getCustomIcon(item.icon, iconSize)}
              </Box>
            ))}
          </Box>
        </>
      )}
      {action && (
        <Box sx={{ gridColumn: 2, gridRow: 1, alignSelf: 'center' }}>
          {action}
        </Box>
      )}
    </Box>
  );
};

export default FormMessageList;
